/** Gaussian blur — separable 1D convolution.
 *
 *  A 2D Gaussian kernel is separable: convolving rows then columns
 *  with the same 1D kernel is mathematically equivalent to
 *  convolving with the full 2D kernel, but the cost drops from
 *  O(W·H·r²) to O(W·H·r). At r=20 that's a ~20× speedup over the
 *  naive 2D pass; without it the live preview would tank on
 *  anything above a 768²-bounded source.
 *
 *  Sigma is tied to radius via σ ≈ radius/3 (the usual "three-
 *  sigma rule" cutoff — pixels >3σ from center contribute <0.3% of
 *  the weight, so the kernel is effectively finite at 2r+1 taps).
 *
 *  Edge handling clamps to the source edge — the standard for blur
 *  filters. Wrapping would bleed pixels across the canvas seam;
 *  zero-pad would darken the perimeter. Clamp is the user-intent
 *  default for image-editor blur. */

export interface GaussianBlurValue {
  /** Radius in pixels. 0 = identity (no blur). Caps at 200 in the
   *  UI; the math itself is bounded only by performance. */
  radius: number;
}

export const identityGaussianBlurValue = (): GaussianBlurValue => ({ radius: 0 });

/** Build a normalized 1D Gaussian kernel of length 2r+1.
 *  Weights sum to 1.0 exactly (within FP rounding) — important so
 *  the convolution preserves average brightness rather than dimming
 *  the image. */
export function buildGaussianKernel1D(radius: number): Float32Array {
  const r = Math.max(0, Math.floor(radius));
  if (r === 0) return new Float32Array([1]);
  const sigma = r / 3;
  const twoSigmaSq = 2 * sigma * sigma;
  const size = 2 * r + 1;
  const kernel = new Float32Array(size);
  let sum = 0;
  for (let i = -r; i <= r; i++) {
    const w = Math.exp(-(i * i) / twoSigmaSq);
    kernel[i + r] = w;
    sum += w;
  }
  // Normalize.
  for (let i = 0; i < size; i++) kernel[i] /= sum;
  return kernel;
}

/** Horizontal 1D convolution: each output pixel reads x±r from the
 *  same row. Edge-clamped on both ends. Writes RGB independently,
 *  copies alpha straight through — blurring alpha causes ghost
 *  halos around already-transparent layer regions, which never
 *  matches user intent for image-content blur.
 *
 *  Split from the vertical pass (rather than branching inside the
 *  inner loop on a `horizontal` flag) so V8 can monomorphize each
 *  hot loop and the tap calculation is a single `Math.min/max`
 *  rather than a branch per kernel tap. */
function convolveHorizontal(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  width: number,
  height: number,
  kernel: Float32Array,
): void {
  const r = (kernel.length - 1) >> 1;
  const maxX = width - 1;
  for (let y = 0; y < height; y++) {
    const rowOffset = y * width;
    for (let x = 0; x < width; x++) {
      let rAcc = 0;
      let gAcc = 0;
      let bAcc = 0;
      for (let k = -r; k <= r; k++) {
        const sx = Math.min(maxX, Math.max(0, x + k));
        const si = (rowOffset + sx) * 4;
        const w = kernel[k + r];
        rAcc += src[si] * w;
        gAcc += src[si + 1] * w;
        bAcc += src[si + 2] * w;
      }
      const di = (rowOffset + x) * 4;
      dst[di] = rAcc;
      dst[di + 1] = gAcc;
      dst[di + 2] = bAcc;
      dst[di + 3] = src[di + 3];
    }
  }
}

/** Vertical 1D convolution: each output pixel reads y±r from the
 *  same column. See `convolveHorizontal` for the rationale behind
 *  having two specialised functions instead of one branchy one. */
function convolveVertical(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  width: number,
  height: number,
  kernel: Float32Array,
): void {
  const r = (kernel.length - 1) >> 1;
  const maxY = height - 1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let rAcc = 0;
      let gAcc = 0;
      let bAcc = 0;
      for (let k = -r; k <= r; k++) {
        const sy = Math.min(maxY, Math.max(0, y + k));
        const si = (sy * width + x) * 4;
        const w = kernel[k + r];
        rAcc += src[si] * w;
        gAcc += src[si + 1] * w;
        bAcc += src[si + 2] * w;
      }
      const di = (y * width + x) * 4;
      dst[di] = rAcc;
      dst[di + 1] = gAcc;
      dst[di + 2] = bAcc;
      dst[di + 3] = src[di + 3];
    }
  }
}

export function applyGaussianBlurPixels(source: ImageData, value: GaussianBlurValue): ImageData {
  const w = source.width;
  const h = source.height;
  const r = Math.max(0, Math.floor(value.radius));
  if (r === 0) {
    // Identity — return a verbatim copy. Callers are expected to
    // treat the output as immutable, but we still allocate a fresh
    // buffer to keep the "doesn't share storage with source" invariant
    // every other adjustment provides (lets future composite paths
    // chain filters without copy-on-write tracking).
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const kernel = buildGaussianKernel1D(r);
  const temp = new Uint8ClampedArray(source.data.length);
  const out = new Uint8ClampedArray(source.data.length);
  convolveHorizontal(source.data, temp, w, h, kernel);
  convolveVertical(temp, out, w, h, kernel);
  return new ImageData(out, w, h);
}

export function applyGaussianBlurToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: GaussianBlurValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyGaussianBlurPixels(source, value), 0, 0);
}
